import { baseApi } from '@/store/baseApi';
import type { IVersion, IDocument, IDepartment } from '@/features/Docs&Policies/types/interfaces';
import { unwrapProseMirrorDoc } from '@/components/editor/proseMirrorUtils';
import type { JSONContent } from '@tiptap/react';

export type DocumentApiStatus = 'draft' | 'inReview' | 'approved' | 'reviewOverdue';

export type Stats = {
  total: number;
  approved: number;
  inReview: number;
  draft: number;
  reviewOverdue: number;
};

export type GetDocumentsParams = {
  page?: number;
  limit?: number;
  sortOrder?: 'ASC' | 'DESC';
  sortBy?: string;
  type?: 'SOP' | 'Policy';
  status?: DocumentApiStatus;
  searchText?: string;
  searchColumns?: string;
  department?: string;
  departmentName?: string;
};

export type DocumentsApiResponse = {
  items: IDocument[];
  total: number;
  page: number;
  limit: number;
  stats: Stats;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

// GET /m4/documents/all's item shape — leaner than IDocument (raw ownerId/approverId, no nested
// owner/approver/latestVersion objects; that richer shape is /m4/documents-only).
export type DocumentSummary = {
  id: string;
  title: string;
  type: string;
  department: string | null;
  ownerId: string;
  approverId: string | null;
  accessLevel: string;
  status: string;
  reviewFrequency: string | null;
  nextReviewDate: string | null;
  lastReviewDate: string | null;
  frameworkControls: unknown;
  description: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  departmentDetails: { name: string } | null;
};

export type GetAllDocumentsParams = {
  type?: 'SOP' | 'Policy';
};

export type GetAllDepartmentsParams = {
  scope?: 'RISK_CREATION';
};

export type DocumentStatsResponse = {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
};

export interface IGetDocumentContentRequest {
  documentId: string;
  versionId: string;
}
export interface ITextNode {
  type: 'text';
  text: string;
}

export interface IDocumentNode {
  type: 'heading' | 'paragraph' | string;
  content?: ITextNode[];
}

export interface IProseMirrorDoc {
  type: 'doc';
  content?: IDocumentNode[];
}

export interface IGetDocumentContentResponse {
  content: IProseMirrorDoc;
  isIntermediate: boolean;
}

export interface ICreateDocumentRequest {
  title: string;
  type: 'SOP' | 'Policy';
  department: string;
  ownerId: string;
  accessLevel: string; // 'DepartmentSpecific' | 'OrganisationWide' | 'Restricted';
  reviewFrequency: string;
  description?: string;
  frameworkControls?: string;
  versionNumber: string;
  currentApprovedDate?: string;
  documentId: string | null;
  controlIds: string[];
}

export interface IPutDocumentContentRequest {
  isDraft: boolean;
  documentId: string;
  versionId: string;
  content: JSONContent;
}

const documentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDocuments: builder.query<DocumentsApiResponse, GetDocumentsParams>({
      query: ({
        page = 1,
        limit = 10,
        sortOrder = 'DESC',
        sortBy = 'createdAt',
        type,
        status,
        searchText,
        searchColumns,
        department,
        departmentName,
      } = {}) => ({
        url: '/m4/documents',
        params: {
          page,
          limit,
          sortOrder,
          sortBy,
          ...(type && { type }),
          ...(status && { status }),
          ...(searchText && { searchText }),
          ...(searchColumns && { searchColumns: searchColumns.toLowerCase() }),
          ...(department && department !== 'all' && { department }),
          ...(departmentName && { departmentName }),
        },
      }),
      transformResponse: (response: { data: DocumentsApiResponse }) => response.data,
      providesTags: ['Document'],
    }),
    getDocumentStats: builder.query<DocumentStatsResponse, void>({
      query: () => ({ url: '/m4/documents/stats' }),
      transformResponse: (response: { data: DocumentStatsResponse }) => response.data,
      providesTags: ['DocumentStats'],
    }),
    getDocumentById: builder.query<IDocument, string>({
      query: (documentId) => ({ url: `/m4/documents/${documentId}` }),
      transformResponse: (response: { data: IDocument }) => response.data,
      providesTags: ['Document'],
    }),
    getDocumentVersions: builder.query<IVersion[], string>({
      query: (documentId) => ({ url: `/m4/documents/${documentId}/versions` }),
      transformResponse: (response: { data: { items: IVersion[] } }) => response.data.items,
    }),
    getDocumentContent: builder.query<IGetDocumentContentResponse, IGetDocumentContentRequest>({
      query: ({ documentId, versionId }: IGetDocumentContentRequest) => ({
        url: `/m4/documents/${documentId}/versions/${versionId}/content`,
      }),
      transformResponse: (response: {
        data: { content: unknown; isIntermediate: boolean };
      }): IGetDocumentContentResponse => ({
        content: unwrapProseMirrorDoc(response.data.content) as IProseMirrorDoc,
        isIntermediate: response.data.isIntermediate,
      }),
      providesTags: (_result, _error, { documentId, versionId }) => [
        { type: 'Document', id: `content-${documentId}-${versionId}` },
      ],
    }),
    getAllDepartments: builder.query<IDepartment[], GetAllDepartmentsParams | void>({
      query: (params) => ({
        url: '/m4/departments',
        params: params?.scope ? { scope: params.scope } : undefined,
      }),
      transformResponse: (response: { data: IDepartment[] }) => response.data,
    }),
    getDepartmentHeads: builder.query<IDepartment[], void>({
      query: () => ({ url: '/m4/departments/control-owners' }),
      transformResponse: (response: { data: IDepartment[] }) => response.data,
    }),
    // Unpaginated, non-draft SOPs/Policies, alphabetical — for picker UIs (e.g. RiskManagement's
    // "Policy/procedure reference" select), not the paginated registry table. Permission is
    // READ_CONTROL_LIBRARY, unlike every other endpoint in this file (READ_DOCUMENT_REGISTRY) —
    // the backend scopes it to the Controls module's permission instead.
    getAllDocuments: builder.query<DocumentSummary[], GetAllDocumentsParams | void>({
      query: (params) => ({
        url: '/m4/documents/all',
        params: params?.type ? { type: params.type } : undefined,
      }),
      transformResponse: (response: { data: DocumentSummary[] }) => response.data ?? [],
      providesTags: ['Document'],
    }),
    createDocument: builder.mutation<IDocument, ICreateDocumentRequest>({
      query: (body) => ({ url: '/m4/documents', method: 'POST', data: body }),
      transformResponse: (response: { data: IDocument }) => response.data,
      invalidatesTags: ['DocumentStats', 'Document'],
    }),
    putDocumentContent: builder.mutation<void, IPutDocumentContentRequest>({
      query: ({ documentId, versionId, content, isDraft }: IPutDocumentContentRequest) => ({
        url: `/m4/documents/${documentId}/versions/${versionId}/content`,
        method: 'PUT',
        data: { content },
        params: { isDraft },
      }),
      invalidatesTags: (_result, _error, { documentId, versionId }) => [
        { type: 'Document', id: `content-${documentId}-${versionId}` },
        'Document',
        'DocumentStats',
      ],
    }),
    downloadPdf: builder.mutation<Blob, { documentId: string; versionId: string }>({
      query: ({ documentId, versionId }) => ({
        url: `/m4/documents/${documentId}/versions/${versionId}/pdf`,
        method: 'GET',
        responseType: 'blob',
      }),
    }),
  }),
});

export const {
  useGetDocumentsQuery,
  useGetDocumentStatsQuery,
  useGetDocumentByIdQuery,
  useGetDocumentVersionsQuery,
  useGetDocumentContentQuery,
  useGetAllDepartmentsQuery,
  useGetDepartmentHeadsQuery,
  useGetAllDocumentsQuery,
  useLazyGetAllDepartmentsQuery,
  useCreateDocumentMutation,
  usePutDocumentContentMutation,
  useDownloadPdfMutation,
} = documentsApi;
